import type { ColumnDef } from "@tanstack/react-table";
import { noop } from "lodash";

import { CurrencyCell } from "@/components/shared/cells/currency";
import { DataTableColumnHeader } from "@/components/shared/tables/ColumnHeader";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { LinkCell } from "@/components/shared/cells/link";
import { useTenant } from "@/providers/session";

import { AccountsCell } from "@/components/shared/cells/accounts";
import { SupplierCell } from "@/components/shared/cells/supplier";
import type { QueryResponse } from "../types";

export const columns: ColumnDef<QueryResponse>[] = [
  {
    id: "link",
    size: 30,
    header: () => null,
    cell: ({ row }) => {
      const invoiceId = row.original.invoice.invoiceId;
      const { companySlug } = useTenant();

      const href = `/${companySlug}/buchhaltung/rechnung/${invoiceId}`;

      return (
        <div className="w-full h-full flex items-center justify-center">
          <LinkCell href={href} />
        </div>
      );
    },
  },
  {
    accessorKey: "invoice.supplier.supplierName",
    id: "supplierName",
    // size: 100,
    enableResizing: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Kreditor" />
    ),
    cell: ({ row }) => {
      const supplierName = row.original.supplier?.supplierName || "Unbekannt";
      const supplierAddress = row.original.supplier?.address ?? undefined;

      return <SupplierCell name={supplierName} address={supplierAddress} />;
    },
  },
  {
    id: "lineItemDescription",
    accessorKey: "description",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Leistung" />
    ),
    cell: ({ row }) => {
      const lineItems = row.original.lineItemDetails;

      const descriptions = new Set<string>();

      for (const item of lineItems) {
        const description = item.lineItem.description ?? item.lineItem.name;
        if (description) {
          descriptions.add(description);
        }
      }

      let displayText = "Unbekannt";
      if (descriptions.size > 1) {
        displayText = row.original?.invoice?.aiSummary || "Mehrere Leistungen";
      } else if (descriptions.size === 1) {
        displayText = Array.from(descriptions)[0];
      }

      return (
        <Tooltip>
          <TooltipTrigger
            style={{
              width: "100%",
              display: "flex",
            }}
          >
            <div className="overflow-hidden whitespace-nowrap text-ellipsis flex-1 text-start">
              <span>{displayText}</span>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <div className="tooltip-maxwidth whitespace-normal text-wrap">
              {Array.from(descriptions).map((desc, index) => (
                <div key={index.toString()}>
                  <span>{desc}</span>
                </div>
              ))}
            </div>
          </TooltipContent>
        </Tooltip>
      );
    },
  },
  {
    id: "amount",
    accessorKey: "amount",
    size: 100,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Betrag" />
    ),
    cell: ({ row }) => {
      const { currency, totalAmount } = row.original.invoice;

      if (!totalAmount || !currency) {
        return null;
      }

      return <CurrencyCell value={totalAmount} currency={currency} />;
    },
  },
  {
    accessorKey: "expenseAccount.accountId",
    id: "expenseAccount",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Sachkonto" />
    ),
    cell: ({ row }) => {
      const lineItems = row.original.lineItemDetails ?? [];

      const lineItemsIds = row.original.lineItemDetails.map(
        (lineItemDetail) => lineItemDetail.lineItem.lineItemId,
      );

      const accounts = new Map<
        string,
        { accountName: string; accountNo: string; justification: string }
      >();

      for (const item of lineItems) {
        const accountId = item.expenseAccount?.accountId || "Unbekannt";
        const accountName = item.expenseAccount?.accountName || "Unbekannt";
        const accountNo = item.expenseAccount?.accountNo || "Unbekannt";
        const justification = item.lineItem.accountJustification || "";

        // If account name is not already in the map, add it with its number
        if (!accounts.has(accountId)) {
          accounts.set(accountId, {
            accountName,
            accountNo,
            justification,
          });
        }
      }

      const accountIds = Array.from(accounts.keys());

      const tooltipDescription = Array.from(accounts.entries()).map(
        ([accountId, { accountName, accountNo, justification }]) => ({
          name: accountName,
          number: accountNo,
          justification,
        }),
      );

      return (
        <AccountsCell
          readOnly
          onAccountChange={noop}
          lineItemIds={lineItemsIds}
          accountIds={accountIds}
          tooltipDescription={tooltipDescription}
        />
      );
    },
  },
];
